import {
  applyTheme,
  getStoredTheme,
  resolveTheme,
  setTheme,
  watchSystemTheme,
  ThemeMode,
  type ResolvedThemeMode,
} from '@shigo-ui/theme';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { ShigoThemeContext, type ShigoThemeContextValue } from './theme-context';

export function ShigoThemeProvider({ children }: { children: ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => getStoredTheme());
  const [systemThemeRevision, setSystemThemeRevision] = useState(0);
  const resolvedTheme = useMemo<ResolvedThemeMode>(() => {
    void systemThemeRevision;
    return resolveTheme(themeMode);
  }, [systemThemeRevision, themeMode]);
  const setThemeMode = useCallback((nextThemeMode: ThemeMode) => {
    setThemeModeState(nextThemeMode);
    setTheme(nextThemeMode);
  }, []);

  useEffect(() => {
    applyTheme(themeMode);

    if (themeMode !== ThemeMode.System) {
      return;
    }

    return watchSystemTheme(() => {
      applyTheme(ThemeMode.System);
      setSystemThemeRevision((currentRevision) => currentRevision + 1);
    });
  }, [themeMode]);

  const value = useMemo<ShigoThemeContextValue>(
    () => ({ resolvedTheme, themeMode, setThemeMode }),
    [resolvedTheme, setThemeMode, themeMode],
  );

  return <ShigoThemeContext.Provider value={value}>{children}</ShigoThemeContext.Provider>;
}
